import clsx from 'clsx';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useAuthContext } from '@/contexts/auth-context';
import { useWxCopilotPageContext } from '@/hooks/use-wx-copilot-page-context';

import { useWxCopilotContext } from './context';
import useWxCopilot from './use-wx-copilot';

// Routes where copilot should be hidden (routes outside Layout)
const HIDDEN_COPILOT_ROUTES = ['/post-job', '/reach'];

export type WxCopilotHostProps = {
  id?: string;
  companyUuid?: string | null;
  product?: string;
  panelClassName?: string;
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
  locale?: string;
  onPanelEvent?: (event: { isOpen: boolean }) => void;
  onToolEvent?: (event: { tool: string }) => void;
};

export type WxCopilotHostHandle = {
  sendMessageToQueue: (payload: unknown) => void;
  toggleCopilotPanel: () => void;
  destroy: () => void;
};

function WxCopilotHostInner(
  _props: WxCopilotHostProps,
  ref: React.Ref<WxCopilotHostHandle>,
) {
  const {
    setIsPanelOpen,
    setIsCopilotRendered,
    isPanelOpen,
    recordLastToolCallName,
  } = useWxCopilotContext();
  const { whoami } = useAuthContext();
  const location = useLocation();

  // Check if copilot should be hidden for current route
  const shouldHideCopilot = HIDDEN_COPILOT_ROUTES.some(route =>
    location.pathname.includes(route),
  );

  // Mark copilot as rendered on mount (only when visible)
  useEffect(() => {
    setIsCopilotRendered(!shouldHideCopilot);
    return () => setIsCopilotRendered(false);
  }, [setIsCopilotRendered, shouldHideCopilot]);

  // Handle copilot panel events
  const handlePanelEvent = useCallback(
    (event: { isOpen: boolean }) => {
      setIsPanelOpen(event.isOpen);
    },
    [setIsPanelOpen],
  );

  const handleToolEvent = useCallback(
    (event: { type: string; toolName: string }) => {
      const tool = event.toolName?.toLowerCase() ?? '';
      // Record the tool name after a mutation (POST, PUT or PATCH)
      if (
        event.type === 'tool_execution_complete' &&
        (tool.startsWith('post') ||
          tool.startsWith('put') ||
          tool.startsWith('patch'))
      ) {
        recordLastToolCallName(event.toolName);
      }
    },
    [recordLastToolCallName],
  );

  // Memoize WxCopilot render props to prevent unnecessary re-renders
  // Similar to navbarConfig in RootContainer - prevents UMD mount node from being cleared
  const wxCopilotProps = useMemo(
    () => ({
      companyUuid: whoami?.account_wx_external_id,
      product: 'source',
      panelClassName: 'fixed top-0 right-0 h-full z-50',
      buttonClassName: 'h-10',
      buttonStyle: { maxWidth: 'none' },
      locale: whoami?.wx_navbar_config?.locale,
      onPanelEvent: handlePanelEvent,
      onToolEvent: handleToolEvent,
    }),
    [
      handlePanelEvent,
      whoami?.account_wx_external_id,
      whoami?.wx_navbar_config?.locale,
      handleToolEvent,
    ],
  );

  const mountId = 'wx-copilot-root';
  const internalRef = useRef<HTMLDivElement>(null);
  const mountRef = internalRef;

  // call hook (hook will use mountRef and renderProps)
  useWxCopilot({
    mountRef,
    mountId,
    renderProps: wxCopilotProps,
  });

  // Track page context changes
  useWxCopilotPageContext(mountId);

  // expose instance methods via ref (1:1 with UMD's instance API)
  useImperativeHandle(
    ref,
    () => ({
      sendMessageToQueue: (payload: unknown) =>
        window.WxCopilotComponentInstances?.[mountId]?.sendMessageToQueue?.(
          payload,
        ),
      toggleCopilotPanel: () =>
        window.WxCopilotComponentInstances?.[mountId]?.toggleCopilotPanel?.(),
      destroy: () => window.WxCopilotComponentInstances?.[mountId]?.destroy?.(),
    }),
    [mountId],
  );

  return (
    <div
      className={clsx(
        'fixed top-2 right-6 z-40',
        isPanelOpen && 'right-[40px]',
        shouldHideCopilot && 'hidden',
      )}
      id={mountId}
      ref={mountRef}
    />
  );
}

const WxCopilotHost = forwardRef(WxCopilotHostInner);

// IMPORTANT: When embedding the UMD component inside a React app, wrap the host wrapper
// with React.memo to prevent parent re-renders from accidentally remounting the provider
// tree and Copilot internals. This avoids a class of subtle bugs where host state updates
// (for example, calling setIsPanelOpen in an onPanelEvent handler) cause the copilot
// panel to never mount.
const MemoWxCopilotHost = React.memo(WxCopilotHost);
export default MemoWxCopilotHost;
